import { useDispatch, useSelector } from "react-redux";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";
import { useEffect, useRef, useState } from "react";
import { EnvironmentService } from "../../services/EnvironmentService";
import {
  initializeTranslatedEnvironments,
  selectIsTranslatedEnvironmentsInitialized,
  selectTranslatedEnvironments,
  selectTranslatedEnvironmentsSearch,
  selectTranslatedEnvironmentsTotalPages,
  updateTranslatedEnvironments,
} from "../../app/slices/EnvironmentSlice";

/**
 * Lazily pages the organization's environments into the store. `search`
 * filters by name server-side; changing it refetches from page 1.
 */
export default function useTranslatedEnvironments(search: string = "") {
  const dispatch = useDispatch();
  const translatedEnvironments = useSelector(selectTranslatedEnvironments);

  const token = useSelector(selectToken);
  const currentOrganization = useSelector(selectCurrentOrganization);

  const isInitialized = useSelector(selectIsTranslatedEnvironmentsInitialized);
  const cachedSearch = useSelector(selectTranslatedEnvironmentsSearch);
  const totalPages = useSelector(selectTranslatedEnvironmentsTotalPages);

  const [currentPage, setCurrentPage] = useState(1);

  // Responses for a search the user has already typed past are dropped.
  const latestSearchRef = useRef(search);
  latestSearchRef.current = search;

  const pageSize = 3;

  useEffect(() => {
    if (!isInitialized || cachedSearch !== search) initialize();
  }, [search]);

  const initialize = async () => {
    const requestedSearch = search;
    setCurrentPage(1);
    var result = await EnvironmentService.GetTranslatedEnvironments(
      currentOrganization!.id,
      token!,
      "en",
      "US",
      1,
      pageSize,
      "name",
      requestedSearch
    );
    if (!result || requestedSearch !== latestSearchRef.current) return;

    dispatch(
      initializeTranslatedEnvironments({
        totalPages: result.totalPages,
        totalCount: result.totalCount,
        firstPage: result.translatedEnvironments,
        search: requestedSearch,
      })
    );
  };

  const fetchPage = async (page: number) => {
    if (!currentOrganization || !token) return;
    const requestedSearch = search;

    // TODO: get lang + country code from the editor !
    var envs = await EnvironmentService.GetTranslatedEnvironments(
      currentOrganization.id,
      token,
      "en",
      "US",
      page,
      pageSize,
      "name",
      requestedSearch
    );

    dispatch(
      updateTranslatedEnvironments({
        pageNumber: page,
        pageSize: pageSize,
        page: envs?.translatedEnvironments ?? [],
        search: requestedSearch,
      })
    );
  };

  const loadNextPage = () => {
    // if at the end, don't do anything
    if (currentPage >= totalPages) return;

    if (translatedEnvironments[currentPage * pageSize].id === "loading") {
      fetchPage(currentPage + 1);
      setCurrentPage(currentPage + 1);
    }
  };

  const onRerender = async (
    amountOfColumns: number,
    rowStartIndex: number,
    rowStopIndex: number
  ) => {
    // pages belong to the cached search; wait until the new search is in
    if (cachedSearch !== search) return;

    var startIndex = amountOfColumns * rowStartIndex;
    var endIndex = amountOfColumns * rowStopIndex + (amountOfColumns - 1);
    var startPage = Math.floor(startIndex / pageSize);
    var endPage = Math.ceil(endIndex / pageSize);

    for (var s = startPage; s < endPage; s++) {
      if (translatedEnvironments[pageSize * s]?.id === "loading") {
        await fetchPage(s + 1);
      }
    }
  };

  return { loadNextPage, onRerender };
}
